import { engineComponent, NumberUtils, Utils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { Communication } from "neo-games";
import { Winframes } from "../GameComponents/Winframes";
import { CustomUtils } from "../Utils/CustomUtils";
import { Symbol } from "../GameComponents/Symbol";
import { ReelExtended } from "./ReelExtended";
import { Payline } from "../GameComponents/Payline";
import { PaylinesDefinitions } from "../GameComponents/PaylinesDefinitions";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { BaseView } from "../GameComponents/Base/BaseView";
import { GameManager } from "../Managers/GameManager";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { AppContext } from "../../Common/AppContext";
import { BetManager } from "../Managers/BetManager";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { Languages } from "../Utils/Languages";
import { PlayList } from "../Components/Sounds/PlayList";
import { SymbolWild } from "./SymbolWild";

type IWin = Communication.Game.IWin;

type wildSymbols = {
    type: string;
    reelIndex: number;
    symbolIndex: number;
    multiplier?: number;
};

export enum CopyWildDelays {
    POP_TO_SPAWN = 25,
    SPAWN_2 = 25,
    SPAWNED = 25,
}

export enum ExpandWildDelays {
    POP_TO_EXPAND = 30,
    SPAWN_2 = 25,
    SPAWNED = 5,
}

export enum MultiplyWildDelays {
    POP_TO_EXPAND = 5,
    SPAWN_2 = 60,
    SPAWNED = 30,
}

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    public static Force: boolean = false;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    public noWinExpandedSymbolsContainer: ContainerElement;
    public expandedSymbolsContainer: ContainerElement;
    public wildSymbolsContainer: ContainerElement;

    protected winDelay: number = 5;
    protected elementInstance: Object = {};

    // copy
    protected _processWildCopyTimer: FrameTimerEventEmitter;
    protected _spawnWildCopyTimer: FrameTimerEventEmitter;
    protected _addCopyWildOnSpawnedPlaceTimer: FrameTimerEventEmitter;

    // expand
    protected _processWildExpandTimer: FrameTimerEventEmitter;
    protected _spawnWildExpandTimer: FrameTimerEventEmitter;
    protected _addExpandWildOnSpawnedPlaceTimer: FrameTimerEventEmitter;

    // multiply
    protected _processWildMultiplyTimer: FrameTimerEventEmitter;
    protected _spawnWildMultiplyTimer: FrameTimerEventEmitter;
    protected _addMultiplyWildOnSpawnedPlaceTimer: FrameTimerEventEmitter;

    // cloning wilds
    protected cloningSymbols: SymbolWild[];
    protected preparedWildSymbolsToSpawn: wildSymbols[];
    protected processWildCopyCnt: number;
    protected wildCopySpawnCnt: number;
    protected currentWildCopySpawnCnt: number;
    protected wildsCopyParts: number[];

    // expanding wilds
    protected expandingSymbols: SymbolWild[];
    protected processWildExpandCnt: number;
    protected wildExpandSpawnCnt: number;
    protected currentWildExpandSpawnCnt: number;
    protected wildsExpandParts: number[];

    // multiply wilds
    protected multiplyingSymbols: SymbolWild[];
    protected processWildMultiplyCnt: number;
    protected wildMultiplySpawnCnt: number;
    protected currentWildMultiplySpawnCnt: number;
    protected wildsMultiplyParts: number[];

    //******************************************************************************************************************
    //  CREATE, INITIALIZE
    //******************************************************************************************************************

    public create(node: Element): void {
        super.create(node);

        //copy wild
        this._processWildCopyTimer = CreateFrameTimer("processWildCopy");
        this._processWildCopyTimer.on(FrameTimerEvents.Complete, this.processWildCopy.bind(this));

        this._spawnWildCopyTimer = CreateFrameTimer("spawnWildCopy");
        this._spawnWildCopyTimer.on(FrameTimerEvents.Complete, this.spawnWildCopy.bind(this));

        this._addCopyWildOnSpawnedPlaceTimer = CreateFrameTimer("addCopyWild");

        //expand wild
        this._processWildExpandTimer = CreateFrameTimer("processWildExpand");
        this._processWildExpandTimer.on(FrameTimerEvents.Complete, this.processWildExpand.bind(this));

        this._spawnWildExpandTimer = CreateFrameTimer("spawnWildExpand");
        this._spawnWildExpandTimer.on(FrameTimerEvents.Complete, this.spawnWildExpand.bind(this));

        this._addExpandWildOnSpawnedPlaceTimer = CreateFrameTimer("addExpandWildOnSpawnedPlace");

        //multiply wild
        this._processWildMultiplyTimer = CreateFrameTimer("processWildMultiply");
        this._processWildMultiplyTimer.on(FrameTimerEvents.Complete, this.processWildMultiply.bind(this));

        this._spawnWildMultiplyTimer = CreateFrameTimer("spawnWildMultiply");
        this._spawnWildMultiplyTimer.on(FrameTimerEvents.Complete, this.spawnWildMultiply.bind(this));

        this._addMultiplyWildOnSpawnedPlaceTimer = CreateFrameTimer("addMultiplyWildOnSpawnedPlace");
    }

    //******************************************************************************************************************
    //  ON REEL STOPPED, SHOW SYMBOLS
    //******************************************************************************************************************

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        super.onReelStopped(reelsData, reelIndex);

        if (reelsData[reelIndex].hasOwnProperty("transform")) {
            let transformData = reelsData[reelIndex].transform;
            if (transformData != null) {
                if (
                    transformData.indexOf(CustomUtils.SymbolKnight) > -1 ||
                    transformData.indexOf(CustomUtils.SymbolWizzard) > -1 ||
                    transformData.indexOf(CustomUtils.SymbolKing) > -1
                ) {
                    this.currentReels = reelsData;

                    for (let i = 0; i < reelsData[reelIndex].symbols.length; i++) {
                        this.removeCurrentSymbol(reelIndex, i);
                    }

                    for (let i = reelsData[reelIndex].symbols.length - 1; i >= 0; i--) {
                        this.addCurrentExpandedSymbol(reelIndex, i);
                    }
                }
            }
        }

        this.impactSpecialSymbols(reelsData, reelIndex);

        if (!ReelExtended.Tease) {
            this.impactBonus(reelsData, reelIndex);
            this.impactWilds(reelsData, reelIndex);
        }

        this.showExpandedSymbol(reelsData, reelIndex);

        this.sortChildrenByZindex();
    }

    public onNudgeStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        this.impactSpecialSymbols(reelsData, reelIndex);

        if (!ReelExtended.Tease) {
            this.impactBonus(reelsData, reelIndex);
            this.impactWilds(reelsData, reelIndex);
        }
    }

    public onNudgeReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        super.onReelStopped(reelsData, reelIndex);

        for (let i = 0; i < reelsData[reelIndex].symbols.length; i++) {
            this.removeCurrentSymbol(reelIndex, i);
        }

        for (let i = reelsData[reelIndex].symbols.length - 1; i >= 0; i--) {
            this.addCurrentExpandedSymbol(reelIndex, i);
        }

        // if (!WinAnimatorExtended.Force) {
        //     this.impactSpecialSymbols(reelsData, reelIndex);
        // }

        this.showExpandedSymbol(reelsData, reelIndex);

        this.sortChildrenByZindex();
    }

    public showSymbols(reelsData: Array<Communication.Game.IReel>): void {
        super.showSymbols(reelsData);

        for (let reelIndex = 0; reelIndex < reelsData.length; reelIndex++) {
            if (reelsData[reelIndex].hasOwnProperty("transform")) {
                let transformData = reelsData[reelIndex].transform;
                if (transformData != null) {
                    if (
                        transformData.indexOf(CustomUtils.SymbolKnight) > -1 ||
                        transformData.indexOf(CustomUtils.SymbolWizzard) > -1 ||
                        transformData.indexOf(CustomUtils.SymbolKing) > -1
                    ) {
                        this.currentReels = reelsData; // todo Preverit.

                        for (let symbolIndex = 0; symbolIndex < reelsData[reelIndex].symbols.length; symbolIndex++) {
                            this.removeCurrentSymbol(reelIndex, symbolIndex);
                        }

                        for (
                            let symbolIndex = reelsData[reelIndex].symbols.length - 1;
                            symbolIndex >= 0;
                            symbolIndex--
                        ) {
                            this.addCurrentExpandedSymbol(reelIndex, symbolIndex);
                        }
                    }
                }
            }

            this.showExpandedSymbol(reelsData, reelIndex);
        }

        this.sortChildrenByZindex();
    }

    //******************************************************************************************************************
    //  NUDGE SYMBOLS - KNIGHT, WIZARD, KING
    //******************************************************************************************************************

    protected showExpandedSymbol(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        for (let i = 0; i < CustomUtils.NudgeSymbols.length; i++) {
            const symbolId = CustomUtils.NudgeSymbols[i];

            if (reelsData[reelIndex].symbols.indexOf(symbolId) > -1) {
                const symbol = this.currentSymbols[reelIndex][0];

                if (this.noWinExpandedSymbolsContainer != null) {
                    this.noWinExpandedSymbolsContainer.addChild(symbol);
                }

                for (let j = 1; j < this.symbolCount; j++) {
                    this.currentSymbols[reelIndex][j].goToCase(Symbol.CaseEmpty);
                }
            }
        }
    }

    //******************************************************************************************************************
    //  IMPACT
    //******************************************************************************************************************

    private impactSpecialSymbols(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        if (
            (reelIndex < 3 || ReelExtended.Tease) &&
            reelsData[reelIndex].symbols.indexOf(this.getTeasingSymbol()) !== -1
        ) {
            PlayList.play("SND_BaseBonusHit");
        }

        this.tease(reelIndex);

        this.sortChildrenByZindex();
    }

    //******************************************************************************************************************
    //  TEASING
    //******************************************************************************************************************

    protected tease(reelIndex: number): void {
        const count = this.countScatters(reelIndex);
        if (count >= this.getMinTeasingSymbolCount()) {
            this.tease3(reelIndex, count);
        } else if (count === this.getMinTeasingSymbolCount() - 1) {
            this.tease2(reelIndex, count);
        } else {
            this.tease1(reelIndex, count);
        }
    }

    protected countScatters(reelIndex: number): number {
        let count = 0;
        for (let i = 0; i <= reelIndex; i++) {
            if (this.currentReels[i].symbols.indexOf(this.getTeasingSymbol()) !== -1) {
                count++;
            }
        }

        return count;
    }

    protected tease1(reelIndex: number, count: number): void {
        if (this.reelCount - reelIndex - 1 < this.getMinTeasingSymbolCount() - count) {
            return;
        }

        const symbolIndex = this.currentReels[reelIndex].symbols.indexOf(this.getTeasingSymbol());

        if (this.currentSymbols[reelIndex][symbolIndex] != null && symbolIndex !== -1) {
            if (this.allowSymbolGoToCase(reelIndex, symbolIndex, Symbol.CaseImpactWithTease)) {
                this.currentSymbols[reelIndex][symbolIndex].goToCase(Symbol.CaseImpactWithTease);
            }
        }
    }

    protected tease2(reelIndex: number, count: number): void {
        if (this.reelCount - reelIndex - 1 < this.getMinTeasingSymbolCount() - count) {
            for (let i = 0; i < reelIndex; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    if (this.currentSymbols[i][j] != null && this.allowSymbolGoToCase(i, j, Symbol.CaseNormal)) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                    }
                }
            }

            return;
        }

        for (let i = 0; i < reelIndex; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                if (this.currentSymbols[i][j] != null && this.currentSymbols[i][j].id === this.getTeasingSymbol() - 1) {
                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseTease)) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseTease);
                    }
                } else {
                    if (this.currentSymbols[i][j] != null && !WinAnimatorExtended.Force) {
                        if (this.allowSymbolGoToCase(i, j, Symbol.CaseNowin)) {
                            this.currentSymbols[i][j].goToCase(Symbol.CaseNowin);
                        }
                    }
                }
            }
        }

        this.tease3(reelIndex, count);
    }

    protected tease3(reelIndex: number, count: number): void {
        for (let i = 0; i < this.currentSymbols[reelIndex].length; i++) {
            if (
                this.currentSymbols[reelIndex][i] != null &&
                this.currentSymbols[reelIndex][i].id === this.getTeasingSymbol() - 1
            ) {
                if (this.allowSymbolGoToCase(reelIndex, i, Symbol.CaseImpactWithTease)) {
                    this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseImpactWithTease);
                }
            } else {
                if (this.currentSymbols[reelIndex][i] != null && !WinAnimatorExtended.Force) {
                    if (this.allowSymbolGoToCase(reelIndex, i, Symbol.CaseNowin)) {
                        this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseNowin);
                    }
                }
            }
        }
    }

    public getTeasingSymbol(): number {
        return CustomUtils.SymbolScatter;
    }

    public getWildSymbol(): number {
        return CustomUtils.SymbolWild;
    }

    public getExpandingSymbol(): number {
        return CustomUtils.SymbolWildExpand;
    }

    public getMultiplyingSymbol(): number {
        return CustomUtils.SymbolWildMultiply;
    }

    public getCloningSymbol(): number {
        return CustomUtils.SymbolWildCloning;
    }

    protected getMinTeasingSymbolCount(): number {
        return 3;
    }

    protected impactBonus(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        if (reelsData[reelIndex] != null && reelsData[reelIndex].symbols != null) {
            for (let symbolIndex: number = 0; symbolIndex < reelsData[reelIndex].symbols.length; symbolIndex++) {
                if (
                    reelsData[reelIndex].symbols[symbolIndex] === CustomUtils.SymbolScatter &&
                    this.currentSymbols[reelIndex][symbolIndex] != null
                ) {
                    if (reelIndex == 3 && this.countScatters(reelIndex - 1) < 1) {
                        //do nothing
                    } else if (reelIndex == 4 && this.countScatters(reelIndex - 1) < 2) {
                        //do nothing
                    } else {
                        this.currentSymbols[reelIndex][symbolIndex].goToCase(Symbol.CaseImpact);
                        PlayList.play("SND_BaseBonusHit");
                    }
                }
            }
        }
    }

    protected impactWilds(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        if (reelsData[reelIndex] != null && reelsData[reelIndex].symbols != null) {
            for (let i: number = 0; i < reelsData[reelIndex].symbols.length; i++) {
                let symbolId = reelsData[reelIndex].symbols[i];

                if (
                    (symbolId === CustomUtils.SymbolWildExpand ||
                        symbolId === CustomUtils.SymbolWildCloning ||
                        symbolId === CustomUtils.SymbolWildMultiply) &&
                    this.currentSymbols[reelIndex][i] != null
                ) {
                    // if (symbolId === CustomUtils.SymbolWildCloning) {
                    //     this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseImpact);
                    // }

                    PlayList.play("SND_BaseWildReelHit" + reelIndex);
                }
            }
        }
    }

    //******************************************************************************************************************
    // COPY WILD - new flow - START
    //******************************************************************************************************************

    public wildCopy_new(init = false): number {
        let totalDuration = 0;

        let hasCopyWild = CustomUtils.hasCopyWild(this.getCustomDataResponse());
        if (hasCopyWild) {
            this.preparedWildSymbolsToSpawn = this.prepareCopyWildArray();

            this.cloningSymbols = [];
            if (this.currentSymbols != null) {
                for (let reelIndex: number = 0; reelIndex < this.currentSymbols.length; reelIndex++) {
                    for (
                        let symbolIndex: number = 0;
                        symbolIndex < this.currentSymbols[reelIndex].length;
                        symbolIndex++
                    ) {
                        let symbol = this.currentSymbols[reelIndex][symbolIndex] as SymbolWild;
                        if (symbol.id == CustomUtils.SymbolWildCloning - 1) {
                            this.cloningSymbols.push(symbol);
                        }
                    }
                }
            }

            let part = Math.round(this.preparedWildSymbolsToSpawn.length / this.cloningSymbols.length);
            let parts_sum = 0;
            let current_part = 0;
            this.wildsCopyParts = [];

            for (let i = 0; i < this.cloningSymbols.length; i++) {
                if (i < this.cloningSymbols.length - 1) {
                    parts_sum += part;
                    current_part = part;
                } else {
                    current_part = this.preparedWildSymbolsToSpawn.length - parts_sum;
                }
                this.wildsCopyParts.push(current_part);

                totalDuration += CopyWildDelays.POP_TO_SPAWN;
            }

            this.processWildCopyCnt = -1;
            this.wildCopySpawnCnt = -1;
            this.processWildCopy(init);

            this.wildsCopyParts.forEach((value, index) => {
                totalDuration += this.wildsCopyParts[index] * CopyWildDelays.SPAWN_2 + CopyWildDelays.SPAWNED;
            });

            totalDuration += 5; // extra custom delay
        }

        return totalDuration;
    }

    /**
     * Zoradi nove wildy v takom poradi, ako sa maju spawnovat na screene.
     */
    protected prepareCopyWildArray(): wildSymbols[] {
        const customData = this.getCustomDataResponse();
        let wildSymbols: wildSymbols[] = [];

        if (!Utils.isNullOrUndefined(customData)) {
            if (customData.hasOwnProperty("wildSymbols")) {
                if (!Utils.isNullOrUndefined(customData.wildSymbols)) {
                    wildSymbols = (customData.wildSymbols as wildSymbols[]).filter((value) => {
                        if (value.type == "copy") {
                            return true;
                        }
                    });

                    wildSymbols.sort((a: any, b: any) => {
                        if (a.hasOwnProperty("type") && b.hasOwnProperty("type")) {
                            if (a.type == "copy" && b.type == "copy") {
                                // Need we this when the array is filtered for copy wilds?
                                if (a.hasOwnProperty("reelIndex") && b.hasOwnProperty("reelIndex")) {
                                    a.reelIndex = a.reelIndex || 0;
                                    b.reelIndex = b.reelIndex || 0;
                                    //return a.reelIndex - b.reelIndex; // ascending
                                    return b.reelIndex - a.reelIndex; // descending
                                }
                                return 0;
                            }
                        }
                    });
                }
            }
        }

        return wildSymbols;
    }

    protected processWildCopy(init: boolean) {
        this._processWildCopyTimer.stop();

        if (init) {
            this.cloningSymbols.forEach((valueCloningSymbol, indexCloningSymbol) => {
                let symbolsCase = this.cloningSymbols[indexCloningSymbol].getCurrentCase();
                this.cloningSymbols[indexCloningSymbol].goToCase(Symbol.CaseEmpty);
                this.cloningSymbols[indexCloningSymbol].transformed = true;
                this.cloningSymbols[indexCloningSymbol].goToCase(symbolsCase);

                this.preparedWildSymbolsToSpawn.forEach((value, index) => {
                    if (!Utils.isNullOrUndefined(this.preparedWildSymbolsToSpawn[index])) {
                        let reelIndex = this.preparedWildSymbolsToSpawn[index].reelIndex;
                        let symbolIndex = this.preparedWildSymbolsToSpawn[index].symbolIndex;
                        this.addCopyWildOnSpawnedPlace([reelIndex, symbolIndex, Symbol.CaseNormal]);
                    }
                });
            });
        } else {
            this.processWildCopyCnt++;

            if (this.processWildCopyCnt > 0) {
                //this.cloningSymbols[this.processWildCopyCnt - 1].transformed = true;
                this.cloningSymbols[this.processWildCopyCnt - 1].goToCase(Symbol.CaseNormal);
            }

            if (!Utils.isNullOrUndefined(this.cloningSymbols[this.processWildCopyCnt])) {
                this.cloningSymbols[this.processWildCopyCnt].goToCase("POP_TO_SPAWN");
                this.cloningSymbols[this.processWildCopyCnt].transformed = true;

                this.currentWildCopySpawnCnt = -1;

                this._spawnWildCopyTimer.start(CopyWildDelays.POP_TO_SPAWN);

                this._processWildCopyTimer.start(
                    this.wildsCopyParts[this.processWildCopyCnt] * CopyWildDelays.SPAWN_2 + CopyWildDelays.SPAWNED,
                );

                /*if (DEBUG) {
                    console.log('@#', '-------------------------------------------------------');
                    console.log('@#', 'Copy wild no.', this.processWildCopyCnt, 'POP_TO_SPAWN');
                    console.log('@#', 'Copy wild delay', (this.wildsCopyParts[this.processWildCopyCnt] * CopyWildDelays.SPAWN_2) + CopyWildDelays.SPAWNED);
                }*/
            }
        }
    }

    protected spawnWildCopy() {
        this._spawnWildCopyTimer.stop();

        if (this.currentWildCopySpawnCnt + 1 < this.wildsCopyParts[this.processWildCopyCnt]) {
            this.currentWildCopySpawnCnt++;
            this.wildCopySpawnCnt++;

            if (!Utils.isNullOrUndefined(this.preparedWildSymbolsToSpawn[this.wildCopySpawnCnt])) {
                let reelIndex = this.preparedWildSymbolsToSpawn[this.wildCopySpawnCnt].reelIndex;
                let symbolIndex = this.preparedWildSymbolsToSpawn[this.wildCopySpawnCnt].symbolIndex;

                // zobrazim spawn animaciu buduceho wildu
                this.spawnWild(reelIndex, symbolIndex, "SPAWN_2");

                // na mieste spawnu pridam s delayom novy wild
                this._addCopyWildOnSpawnedPlaceTimer.once(
                    FrameTimerEvents.Complete,
                    this.addCopyWildOnSpawnedPlace.bind(this, [reelIndex, symbolIndex, "SPAWNED"]),
                );
                this._addCopyWildOnSpawnedPlaceTimer.start(CopyWildDelays.SPAWN_2);

                if (this.currentWildCopySpawnCnt + 1 < this.wildsCopyParts[this.processWildCopyCnt]) {
                    // zavolam znova cely tento cyklus s delay pre dalsi spawn. Dlzka je cca podobna tej 'SPAWN_2'.
                    this._spawnWildCopyTimer.start(CopyWildDelays.SPAWN_2);

                    /*if (DEBUG) {
                        console.log('@#', '---');
                        console.log('@#', 'Spawn wild no.', this.currentWildCopySpawnCnt, 'SPAWN_2');
                        console.log('@#', 'Spawn wild position', reelIndex, symbolIndex);
                        console.log('@#', 'Spawn wild delay', CopyWildDelays.SPAWN_2);
                    }*/
                }
            }
        }
    }

    protected spawnWild(reelIndex: number, symbolIndex: number, caseType = Symbol.CaseSpawn): void {
        if (this.currentReels[reelIndex].transform != null) {
            const id = this.currentReels[reelIndex].transform[symbolIndex];
            //PlayList.play('SND_BaseWildHitCopy');

            const symbol = this.symbols.getSymbol(id - 1, caseType);
            symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
            symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY;

            if (this.wildSymbolsContainer != null) {
                this.wildSymbolsContainer.addChild(symbol);
            }
        }
    }

    protected addCopyWildOnSpawnedPlace(argArray: any[]) {
        this._addCopyWildOnSpawnedPlaceTimer.stop();

        PlayList.play("SND_BaseWildHitCopy");

        this.removeCurrentSymbol(argArray[0], argArray[1]);
        this.addCopyWild(argArray[0], argArray[1], argArray[2]);
    }

    protected addCopyWild(reelIndex: number, symbolIndex: number, caseType = Symbol.CaseNormal): void {
        if (this.currentSymbols[reelIndex][symbolIndex] === null && this.currentReels[reelIndex].transform != null) {
            const id = this.currentReels[reelIndex].transform[symbolIndex];

            const symbol = this.symbols.getSymbol(id - 1, caseType) as SymbolWild;
            symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
            symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY;

            if (this.symbolsContainer != null) {
                this.symbolsContainer.addChild(symbol);
            }

            this.currentSymbols[reelIndex][symbolIndex] = symbol;
        }
    }

    //******************************************************************************************************************
    // EXPAND WILD - new flow - START
    //******************************************************************************************************************

    public wildExpand_new(init = false): number {
        let totalDuration = 0;

        let hasExpandWild = CustomUtils.hasExpandWild(this.getDataResponse());
        if (hasExpandWild) {
            this.preparedWildSymbolsToSpawn = this.prepareExpandWildArray();
            this.expandingSymbols = [];

            if (this.currentSymbols != null) {
                for (let reelIndex: number = 0; reelIndex < this.currentSymbols.length; reelIndex++) {
                    for (
                        let symbolIndex: number = 0;
                        symbolIndex < this.currentSymbols[reelIndex].length;
                        symbolIndex++
                    ) {
                        let symbol = this.currentSymbols[reelIndex][symbolIndex] as SymbolWild;
                        if (symbol.id == CustomUtils.SymbolWildExpand - 1) {
                            this.expandingSymbols.push(symbol);
                        }
                    }
                }
            }

            let current_part = 3;
            this.wildsExpandParts = [];
            this.expandingSymbols.forEach(() => {
                this.wildsExpandParts.push(current_part);
                //totalDuration += ExpandWildDelays.POP_TO_EXPAND;
            });

            this.processWildExpandCnt = -1;
            this.wildExpandSpawnCnt = -1;
            this.processWildExpand(init);

            this.wildsExpandParts.forEach((value, index) => {
                totalDuration += this.wildsExpandParts[index] * (ExpandWildDelays.SPAWN_2 + ExpandWildDelays.SPAWNED);
            });
        }

        return totalDuration;
    }

    /**
     * Zoradi nove wildy v takom poradi, ako sa maju spawnovat na screene.
     */
    protected prepareExpandWildArray(): wildSymbols[] {
        let wildSymbols: wildSymbols[] = [];

        if (this.currentReels != null) {
            for (let i: number = 0; i < this.currentReels.length; i++) {
                if (this.currentReels[i].symbols != null && this.currentReels[i].transform != null) {
                    if (this.currentReels[i].symbols.indexOf(CustomUtils.SymbolWildExpand) !== -1) {
                        if (this.currentReels[i].transform.indexOf(CustomUtils.SymbolWildExpand) !== -1) {
                            for (let j: number = 0; j < this.currentReels[i].transform.length; j++) {
                                wildSymbols.push({
                                    type: "expand",
                                    reelIndex: i,
                                    symbolIndex: j,
                                });
                            }
                        }
                    }
                }
            }
        }

        return wildSymbols;
    }

    protected processWildExpand(init: boolean) {
        this._processWildExpandTimer.stop();

        if (init) {
            this.expandingSymbols.forEach(() => {
                this.preparedWildSymbolsToSpawn.forEach((value, index) => {
                    if (!Utils.isNullOrUndefined(this.preparedWildSymbolsToSpawn[index])) {
                        let reelIndex = this.preparedWildSymbolsToSpawn[index].reelIndex;
                        let symbolIndex = this.preparedWildSymbolsToSpawn[index].symbolIndex;
                        this.addExpandWildOnSpawnedPlace([reelIndex, symbolIndex, Symbol.CaseNormal]);
                    }
                });
            });
        } else {
            this.processWildExpandCnt++;

            if (!Utils.isNullOrUndefined(this.expandingSymbols[this.processWildExpandCnt])) {
                this.expandingSymbols[this.processWildExpandCnt].goToCase("EXPAND_2");
                this.expandingSymbols[this.processWildExpandCnt].transformed = true;
                this.currentWildExpandSpawnCnt = -1;

                this._spawnWildExpandTimer.start(ExpandWildDelays.POP_TO_EXPAND);
                this._processWildExpandTimer.start(
                    this.wildsExpandParts[this.processWildExpandCnt] *
                        (ExpandWildDelays.SPAWN_2 + ExpandWildDelays.SPAWNED),
                );
            }
        }
    }

    protected spawnWildExpand() {
        this._spawnWildExpandTimer.stop();

        if (this.currentWildExpandSpawnCnt + 1 < this.wildsExpandParts[this.processWildExpandCnt]) {
            this.currentWildExpandSpawnCnt++;
            this.wildExpandSpawnCnt++;

            if (!Utils.isNullOrUndefined(this.preparedWildSymbolsToSpawn[this.wildExpandSpawnCnt])) {
                let reelIndex = this.preparedWildSymbolsToSpawn[this.wildExpandSpawnCnt].reelIndex;
                let symbolIndex = this.preparedWildSymbolsToSpawn[this.wildExpandSpawnCnt].symbolIndex;

                this.expandWildReelEffect(reelIndex);

                // zobrazim spawn miesto
                PlayList.play("SND_BaseWildHitExpand" + reelIndex, 0, false);
                this.spawnWild(reelIndex, symbolIndex, "SPAWN_2");

                // na mieste spawnu pridam s delayom novy wild
                this._addExpandWildOnSpawnedPlaceTimer.once(
                    FrameTimerEvents.Complete,
                    this.addExpandWildOnSpawnedPlace.bind(this, [reelIndex, symbolIndex, "SPAWNED"]),
                );
                this._addExpandWildOnSpawnedPlaceTimer.start(ExpandWildDelays.SPAWN_2);

                if (this.currentWildExpandSpawnCnt + 1 < this.wildsExpandParts[this.processWildExpandCnt]) {
                    // zavolam znova cely tento cyklus s delay pre dalsi spawn
                    this._spawnWildExpandTimer.start(ExpandWildDelays.SPAWNED);
                }
            }
        }
    }

    protected expandWildReelEffect(reelIndex: number) {
        if (BaseView["ExpandWildReel" + (reelIndex + 1)] != null) {
            BaseView["ExpandWildReel" + (reelIndex + 1)].goToCase("EXPAND_SPAWN");
        }
    }

    protected addExpandWildOnSpawnedPlace(argArray: any[]) {
        this._addExpandWildOnSpawnedPlaceTimer.stop();

        //PlayList.play('SND_BaseWildHitExpand');

        this.removeCurrentSymbol(argArray[0], argArray[1]);
        this.addExpandWild(argArray[0], argArray[1], argArray[2]); // SPAWNED trva asi max 25 frms
    }

    protected addExpandWild(reelIndex: number, symbolIndex: number, caseType = Symbol.CaseNormal): void {
        if (this.currentSymbols[reelIndex][symbolIndex] === null && this.currentReels[reelIndex].transform != null) {
            const id = this.currentReels[reelIndex].transform[symbolIndex];
            const symbol = this.symbols.getSymbol(id - 1, SymbolWild.CaseEmpty) as SymbolWild;

            symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
            symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY;
            symbol.transformed = true;
            symbol.goToCase(caseType);

            if (this.symbolsContainer != null) {
                this.symbolsContainer.addChild(symbol);
            }

            this.currentSymbols[reelIndex][symbolIndex] = symbol;

            //PlayList.play('SND_BaseWildHitExpand');
        }
    }

    //******************************************************************************************************************
    // MULTIPLY WILD - new flow - START
    //******************************************************************************************************************

    public wildMultiply_new(init = false): number {
        let totalDuration = 0;

        let hasExpandWild = CustomUtils.hasMultiplyWild(this.getDataResponse());
        if (hasExpandWild) {
            this.preparedWildSymbolsToSpawn = this.prepareMultiplyWildArray();
            this.multiplyingSymbols = [];

            if (this.currentSymbols != null) {
                for (let reelIndex: number = 0; reelIndex < this.currentSymbols.length; reelIndex++) {
                    for (
                        let symbolIndex: number = 0;
                        symbolIndex < this.currentSymbols[reelIndex].length;
                        symbolIndex++
                    ) {
                        let symbol = this.currentSymbols[reelIndex][symbolIndex] as SymbolWild;
                        if (symbol.id == CustomUtils.SymbolWildMultiply - 1) {
                            this.multiplyingSymbols.push(symbol);
                        }
                    }
                }
            }

            let current_part = 1;
            this.wildsMultiplyParts = [];
            this.multiplyingSymbols.forEach(() => {
                this.wildsMultiplyParts.push(current_part);
                totalDuration += MultiplyWildDelays.POP_TO_EXPAND;
            });

            this.processWildMultiplyCnt = -1;
            this.wildMultiplySpawnCnt = -1;
            this.processWildMultiply(init);

            this.wildsMultiplyParts.forEach((value, index) => {
                //totalDuration += this.wildsMultiplyParts[index] * (MultiplyWildDelays.SPAWN_2 + MultiplyWildDelays.SPAWNED);
                totalDuration += this.wildsMultiplyParts[index] * MultiplyWildDelays.SPAWN_2;
            });
        }

        return totalDuration;
    }

    /**
     * Zoradi nove wildy v takom poradi, ako sa maju spawnovat na screene.
     */
    protected prepareMultiplyWildArray(): wildSymbols[] {
        const customData = this.getCustomDataResponse();
        let wildSymbols: wildSymbols[] = [];

        if (!Utils.isNullOrUndefined(customData)) {
            if (customData.hasOwnProperty("wildSymbols")) {
                if (!Utils.isNullOrUndefined(customData.wildSymbols)) {
                    wildSymbols = (customData.wildSymbols as wildSymbols[]).filter((value) => {
                        if (value.type == "multiplier") {
                            return true;
                        }
                    });
                }
            }
        }

        return wildSymbols;
    }

    protected processWildMultiply(init: boolean) {
        this._processWildMultiplyTimer.stop();

        if (init) {
            this.multiplyingSymbols.forEach(() => {
                this.preparedWildSymbolsToSpawn.forEach((value, index) => {
                    if (!Utils.isNullOrUndefined(this.preparedWildSymbolsToSpawn[index])) {
                        let multiplier = this.preparedWildSymbolsToSpawn[index].multiplier;
                        this.multiplyingSymbols[index].goToCase("MULTIPLY_" + multiplier + "_END");
                        this.multiplyingSymbols[index].transformed = true;
                        this.multiplyingSymbols[index].multiplier = multiplier;
                    }
                });
            });
        } else {
            this.processWildMultiplyCnt++;

            if (!Utils.isNullOrUndefined(this.multiplyingSymbols[this.processWildMultiplyCnt])) {
                if (!Utils.isNullOrUndefined(this.preparedWildSymbolsToSpawn[this.processWildMultiplyCnt])) {
                    //let reelIndex = this.preparedWildSymbolsToSpawn[this.processWildMultiplyCnt].reelIndex;
                    //let symbolIndex = this.preparedWildSymbolsToSpawn[this.processWildMultiplyCnt].symbolIndex;

                    let multiplier = this.preparedWildSymbolsToSpawn[this.processWildMultiplyCnt].multiplier;
                    this.multiplyingSymbols[this.processWildMultiplyCnt].goToCase("MULTIPLY_" + multiplier + "_END");
                    this.multiplyingSymbols[this.processWildMultiplyCnt].transformed = true;
                    this.multiplyingSymbols[this.processWildMultiplyCnt].multiplier = multiplier;

                    PlayList.play("SND_BaseWildHitMultiplier");
                }
                this.currentWildMultiplySpawnCnt = -1;

                this._spawnWildMultiplyTimer.start(MultiplyWildDelays.POP_TO_EXPAND);
                //this._processWildMultiplyTimer.start(this.wildsMultiplyParts[this.processWildMultiplyCnt] * (MultiplyWildDelays.SPAWN_2 + MultiplyWildDelays.SPAWNED));
                this._processWildMultiplyTimer.start(
                    this.wildsMultiplyParts[this.processWildMultiplyCnt] * MultiplyWildDelays.SPAWN_2,
                );
            }
        }
    }

    protected spawnWildMultiply() {
        this._spawnWildMultiplyTimer.stop();

        if (this.currentWildMultiplySpawnCnt + 1 < this.wildsMultiplyParts[this.processWildMultiplyCnt]) {
            this.currentWildMultiplySpawnCnt++;
            this.wildMultiplySpawnCnt++;

            if (!Utils.isNullOrUndefined(this.preparedWildSymbolsToSpawn[this.wildMultiplySpawnCnt])) {
                let reelIndex = this.preparedWildSymbolsToSpawn[this.wildMultiplySpawnCnt].reelIndex;
                let symbolIndex = this.preparedWildSymbolsToSpawn[this.wildMultiplySpawnCnt].symbolIndex;

                const symbol = this.symbols.getSymbol(CustomUtils.SymbolWildMultiply - 1, "SPAWN_2");
                symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
                symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY;

                if (this.wildSymbolsContainer != null) {
                    this.wildSymbolsContainer.addChild(symbol);
                }

                //this._spawnWildMultiplyTimer.start(MultiplyWildDelays.SPAWNED);
            }
        } else {
            /*let multiplier = this.preparedWildSymbolsToSpawn[this.processWildMultiplyCnt - 1].multiplier;
            this.multiplyingSymbols[this.processWildMultiplyCnt - 1].goToCase('MULTIPLY_' + multiplier + '_END');*/
        }
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        super.showInitWins(wins, win);
        this.sortChildrenByZindex();
    }

    protected showFirstWins(): void {
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
            this.winPaylineGoToCase(i, Payline.CaseWin);
        }

        if (this.currentWin > 0) {
            this.startAnimation();
        } else {
            this.finishFirstWins();
        }
    }

    //******************************************************************************************************************
    //  CYCLE WINS
    //******************************************************************************************************************

    protected cycleWins(): void {
        if (this.currentWins == null) {
            return;
        } else if (this.currentWins.length === 1) {
            PlayList.play("SND_BaseRecapWinline");

            this.allPaylinesGoToCase(Payline.CaseNowin);

            for (let i = 0; i < this.currentWins.length; i++) {
                this.winPaylineGoToCase(i, Payline.CaseNormal);
            }

            this.winSymbolsGoToCase(0, Symbol.CaseSingleWin, true);
            this.winPaylineGoToCase(this.currentCycle, Payline.CaseWin);
        } else {
            PlayList.play("SND_BaseRecapWinline");

            this.allPaylinesGoToCase(Payline.CaseNowin);

            for (let i = 0; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
                this.winPaylineGoToCase(i, Payline.CaseNormal);
            }

            this.winSymbolsGoToCase(this.currentCycle, Symbol.CaseCycle, true);
            this.winPaylineGoToCase(this.currentCycle, Payline.CaseWin);

            this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;

            this.stopTimer();
            this.startTimer(WinAnimator.CycleWins, this.getCycleWinsDelay());
        }
    }

    protected allowSymbolGoToCase(reelIndex: number, symbolIndex: number, caseType: string): boolean {
        let result = super.allowSymbolGoToCase(reelIndex, symbolIndex, caseType);

        this.sortChildrenByZindex();
        return result;
    }

    protected addCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        super.addCurrentSymbol(reelIndex, symbolIndex);

        this.sortChildrenByZindex();
    }

    protected addCurrentExpandedSymbol(reelIndex: number, symbolIndex: number, initShow: boolean = false): void {
        if (this.currentSymbols[reelIndex][symbolIndex] === null) {
            let id = this.currentReels[reelIndex].symbols[symbolIndex];

            if (this.currentReels[reelIndex].transform != null) {
                id = this.currentReels[reelIndex].transform[symbolIndex];
            }

            const symbol = this.symbols.getSymbol(id - 1, Symbol.CaseNormal);
            symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
            symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY;

            if (this.currentReels[reelIndex].transform != null || initShow) {
                this.expandedSymbolsContainer.addChild(symbol);
            } else {
                this.noWinExpandedSymbolsContainer.addChild(symbol);
            }

            this.currentSymbols[reelIndex][symbolIndex] = symbol;
        }
    }

    protected allSymbolsGoToCase(caseType: string, noWin: boolean): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                let symbol = this.currentSymbols[i][j];
                let noWinSymbolsContainer = this.noWinSymbolsContainer;
                let symbolsContainer = this.symbolsContainer;

                if (!Utils.isNullOrUndefined(symbol)) {
                    if (CustomUtils.NudgeSymbols.indexOf(symbol.id + 1) > -1) {
                        const firstIndex = CustomUtils.getNudgeSymbolFirstIndex(
                            this.symbolCount,
                            this.currentSymbols[i],
                            symbol.id + 1,
                        );
                        symbol = this.currentSymbols[i][firstIndex];
                        noWinSymbolsContainer = this.noWinExpandedSymbolsContainer;
                        symbolsContainer = this.expandedSymbolsContainer;
                        if (this.elementInstance["Winframes"] != null) {
                            <Winframes>this.elementInstance["Winframes"].winframes[i][j].goToCase(caseType);
                        }
                    }
                }

                if (!Utils.isNullOrUndefined(symbol)) {
                    if (
                        symbol.id + 1 == CustomUtils.SymbolWildMultiply &&
                        caseType == Symbol.CaseNowin &&
                        ["MULTIPLY_2", "MULTIPLY_2_END", "MULTIPLY_3", "MULTIPLY_3_END"].indexOf(
                            symbol.getCurrentCase(),
                        ) > -1
                    ) {
                        if (this.allowSymbolGoToCase(i, j, caseType)) {
                            if (["MULTIPLY_2", "MULTIPLY_2_END"].indexOf(symbol.getCurrentCase()) > -1) {
                                symbol.goToCase("NOWIN_MULTI_2");
                            } else if (["MULTIPLY_3", "MULTIPLY_3_END"].indexOf(symbol.getCurrentCase()) > -1) {
                                symbol.goToCase("NOWIN_MULTI_3");
                            }
                        }
                    } else {
                        if (this.allowSymbolGoToCase(i, j, caseType)) {
                            symbol.goToCase(caseType);
                        }
                    }

                    if (noWin) {
                        noWinSymbolsContainer.addChild(symbol);
                    } else {
                        symbolsContainer.addChild(symbol);
                    }
                }
            }
        }
    }

    protected winSymbolsGoToCase(winIndex: number, caseType: string, useValue: boolean): void {
        const win = this.currentWins[winIndex];
        let symbols: Array<Symbol> = [];

        switch (win.trigger) {
            case Communication.Game.WinTrigger.Payline:
                symbols = this.winSymbolsGoToCaseAsPayline(winIndex, caseType);
                break;
            case Communication.Game.WinTrigger.Scatter:
                symbols = this.winSymbolsGoToCaseAsScatter(winIndex, caseType);
                break;
            case Communication.Game.WinTrigger.Way:
                symbols = this.winSymbolsGoToCaseAsWay(winIndex, caseType);
                break;
            default:
                symbols = this.winSymbolsGoToCaseAsCustom(winIndex, caseType);
                break;
        }

        if (useValue && symbols.length > 0 && win.amount > 0) {
            const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                win.paylineId,
                this.paylines.paylineDefinition,
            );

            this.value.visible = true;
            this.value.position.x = symbols[0].position.x + symbols[0].offsetX;
            // this.value.position.y = symbols[0].position.y + symbols[0].offsetY;
            this.value.position.y = definition[0] * this.symbolHeight + this.symbolHeight / 3.5;
            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                this.value.play(parseInt(NumberUtils.devide(win.amount, BetManager.CoinSize).toString()));
            } else {
                ImageTextCounterElement.Round = false;
                this.value.play(parseFloat(Languages.formatText(Languages.FormatMoney, win.amount.toString())));
            }
        } else {
            this.value.visible = false;
        }

        this.sortChildrenByZindex();
    }

    protected winSymbolsGoToCaseAsPayline(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];
        const customData = this.getCustomDataResponse();

        if (this.paylines != null) {
            const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                win.paylineId,
                this.paylines.paylineDefinition,
            );

            if (BaseView.PaylinesIndicator != null) {
                // this.addChild(BaseView.PaylinesIndicator);
                BaseView.PaylinesIndicator.goToCase("HIDE");
            }

            for (let i = 0; i < definition.length; i++) {
                if (win.mask[i] === 1 && this.allowSymbolGoToCase(i, definition[i], caseType)) {
                    let symbol = this.currentSymbols[i][definition[i]];
                    let symbolsContainer = this.symbolsContainer;
                    if (CustomUtils.NudgeSymbols.indexOf(symbol.id + 1) > -1) {
                        const firstIndex = CustomUtils.getNudgeSymbolIndex(
                            this.symbolCount,
                            this.currentReels[i].transform,
                            symbol.id + 1,
                        );
                        symbol = this.currentSymbols[i][firstIndex];
                        symbolsContainer = this.expandedSymbolsContainer;
                        if (this.elementInstance["Winframes"] != null) {
                            <Winframes>this.elementInstance["Winframes"].winframes[i][definition[i]].goToCase(caseType);
                        }
                    }

                    // show multi wild wins x2 x3
                    if (symbol.id == CustomUtils.SymbolWildMultiply - 1) {
                        if (
                            caseType == Symbol.CaseSingleWin ||
                            caseType == Symbol.CaseWin ||
                            caseType == Symbol.CaseCycle
                        ) {
                            if (!Utils.isNullOrUndefined(customData)) {
                                if (!Utils.isNullOrUndefined(customData.wildSymbols)) {
                                    for (let j: number = 0; j < customData.wildSymbols.length; j++) {
                                        if (customData.wildSymbols[j].type == "multiplier") {
                                            if (this.currentReels != null) {
                                                let multiplier = customData.wildSymbols[j].multiplier;
                                                let reelIndex = customData.wildSymbols[j].reelIndex;
                                                let symbolIndex = customData.wildSymbols[j].symbolIndex;

                                                if (i == reelIndex && definition[i] == symbolIndex) {
                                                    let symbolMulti = this.currentSymbols[reelIndex][symbolIndex];
                                                    symbolMulti.goToCase(
                                                        caseType + "_MULTI_" + multiplier,
                                                        false,
                                                        i * this.winDelay,
                                                        i,
                                                    );
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        } else {
                            if (!Utils.isNullOrUndefined(customData)) {
                                if (!Utils.isNullOrUndefined(customData.wildSymbols)) {
                                    for (let j: number = 0; j < customData.wildSymbols.length; j++) {
                                        if (customData.wildSymbols[j].type == "multiplier") {
                                            if (this.currentReels != null) {
                                                let multiplier = customData.wildSymbols[j].multiplier;
                                                let reelIndex = customData.wildSymbols[j].reelIndex;
                                                let symbolIndex = customData.wildSymbols[j].symbolIndex;

                                                if (i == reelIndex && definition[i] == symbolIndex) {
                                                    let symbolMulti = this.currentSymbols[reelIndex][symbolIndex];
                                                    symbolMulti.goToCase(caseType + "_MULTI_" + multiplier);
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    if (symbol.id != CustomUtils.SymbolWildMultiply - 1) {
                        if (
                            caseType == Symbol.CaseSingleWin ||
                            caseType == Symbol.CaseWin ||
                            caseType == Symbol.CaseCycle
                        ) {
                            symbol.goToCase(caseType, false, i * this.winDelay, i);
                        } else {
                            symbol.goToCase(caseType);
                        }
                    }
                    symbolsContainer.addChild(symbol);
                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    public reset(showLastSymbols: boolean): boolean {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                const symbol = this.currentSymbols[i][j];
                if (symbol !== null) {
                    symbol.stopDelayTimer();
                }
            }
        }

        this.resetWildSymbolsContainer();
        return super.reset(showLastSymbols);
    }

    public resetWildSymbolsContainer(): void {
        if (!Utils.isNullOrUndefined(this.wildSymbolsContainer)) {
            const children = this.wildSymbolsContainer.children;
            for (let i = children.length - 1; i >= 0; i--) {
                let child = children[i];
                if (child instanceof Symbol) {
                    this.symbols.returnSymbol(child);
                }
            }
        }
    }

    /**
     *
     * @param {Object} element
     */
    public addElementInstance(element: Object): void {
        for (const property in element) {
            if (element[property] != null) {
                if (element[property].length > 0) {
                    if (this.elementInstance[property] == null) {
                        this.elementInstance[property] = [];
                    }

                    for (let i = 0; i < element[property].length; i++) {
                        this.elementInstance[property].push(element[property][i]);
                    }
                } else {
                    this.elementInstance[property] = element[property];
                }
            }
        }
    }

    /**
     * Sorts children of container by symbols position and zIndex attribute.
     */
    protected sortChildrenByZindex(container?: ContainerElement): void {
        if (!Utils.isNullOrUndefined(this.symbolsContainer)) {
            this.symbolsContainer.children
                .sort((a: any, b: any) => {
                    a.x = a.x || 0;
                    b.x = b.x || 0;

                    return a.x - b.x; // ascending: a - b; descending b - a
                })
                .sort((a: any, b: any) => {
                    a.y = a.y || 0;
                    b.y = b.y || 0;

                    return a.y - b.y; // ascending: a - b; descending b - a
                })
                .sort((a: any, b: any) => {
                    if (a.hasOwnProperty("zIndex") && b.hasOwnProperty("zIndex")) {
                        a.zIndex = a.zIndex || 0;
                        b.zIndex = b.zIndex || 0;

                        return a.zIndex - b.zIndex;
                    }

                    return 0;
                });
        }

        if (!Utils.isNullOrUndefined(this.noWinSymbolsContainer)) {
            this.noWinSymbolsContainer.children.sort((a: any, b: any) => {
                if (a.hasOwnProperty("zIndex") && b.hasOwnProperty("zIndex")) {
                    a.zIndex = a.zIndex || 0;
                    b.zIndex = b.zIndex || 0;

                    return a.zIndex - b.zIndex;
                }

                return 0;
            });
        }

        if (!Utils.isNullOrUndefined(container)) {
            container.children
                .sort((a: any, b: any) => {
                    a.x = a.x || 0;
                    b.x = b.x || 0;

                    return a.x - b.x; // ascending: a - b; descending b - a
                })
                .sort((a: any, b: any) => {
                    a.y = a.y || 0;
                    b.y = b.y || 0;

                    return a.y - b.y; // ascending: a - b; descending b - a
                })
                .sort((a: any, b: any) => {
                    if (a.hasOwnProperty("zIndex") && b.hasOwnProperty("zIndex")) {
                        a.zIndex = a.zIndex || 0;
                        b.zIndex = b.zIndex || 0;

                        return a.zIndex - b.zIndex;
                    }

                    return 0;
                });
        }
    }

    public getDataResponse(): any {
        return GameManager.baseResponse;
    }

    public getCustomDataResponse(): any {
        const customData = GameManager.baseResponse.customData;
        /*if (!Utils.isNullOrUndefined(customData)) {

        }*/

        return customData;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/